/** Terrain delta codec from the original room protocol: LEB128 varints, zig-zag, fixed point. */

export interface LetterState {
  vx: number;
  vy: number;
  av: number;
  burn: number;
  pinned: boolean;
}

export interface TerrainSink {
  clear(cell: number): void;
  debris(cell: number, color: number): void;
  ignite(cell: number): void;
  letter(index: number, state: LetterState | null): void;
  block(index: number, vx: number, vy: number, av: number): void;
  ctx(kind: number, a: number, b: number, c: number, d: number): void;
  heat(cell: number, a: number, b: number, flag: boolean): void;
}

class Writer {
  buf = new Uint8Array(4096);
  n = 0;

  private room(extra: number) {
    if (this.n + extra <= this.buf.length) return;
    const next = new Uint8Array(Math.max(this.buf.length * 2, this.n + extra));
    next.set(this.buf.subarray(0, this.n));
    this.buf = next;
  }

  u(value: number) {
    this.room(10);
    let n = Math.max(0, Math.floor(value));
    while (n >= 128) {
      this.buf[this.n++] = (n % 128) | 128;
      n = Math.floor(n / 128);
    }
    this.buf[this.n++] = n;
  }

  s(value: number) {
    const n = Math.round(value);
    this.u(n >= 0 ? n * 2 : -n * 2 - 1);
  }

  f(value: number, scale: number) {
    this.s(value * scale);
  }
}

class Reader {
  private i = 0;
  constructor(private readonly b: Uint8Array) {}
  get done() {
    return this.i >= this.b.length;
  }
  u(): number {
    let n = 0;
    let scale = 1;
    for (;;) {
      if (this.i >= this.b.length) throw new Error("truncated batch");
      const byte = this.b[this.i++];
      n += (byte & 127) * scale;
      if (byte < 128) return n;
      scale *= 128;
    }
  }
  s(): number {
    const n = this.u();
    return n % 2 ? -(n + 1) / 2 : n / 2;
  }
  f(scale: number): number {
    return this.s() / scale;
  }
}

/** Records cell clears, debris, letters and detached blocks, then emits one batch. */
export class TerrainLog implements TerrainSink {
  private out = new Writer();
  private run = 0;
  private cells: number[] = [];
  private colors = new Map<number, number>();
  private lastCtx = "";

  private flush() {
    if (!this.run || !this.cells.length) {
      this.run = 0;
      return;
    }
    const unique = [...new Set(this.cells)].sort((a, b) => a - b);
    this.out.u(this.run);
    this.out.u(unique.length);
    let prev = 0;
    for (const cell of unique) {
      this.out.u(cell - prev);
      prev = cell;
      if (this.run === 2) this.out.u(this.colors.get(cell)! >>> 0);
    }
    this.cells.length = 0;
    this.colors.clear();
    this.run = 0;
  }

  private into(op: number) {
    if (this.run !== op) this.flush();
    this.run = op;
  }

  private op(code: number) {
    this.flush();
    this.out.u(code);
  }

  clear(cell: number) {
    this.into(1);
    this.cells.push(cell);
  }

  debris(cell: number, color: number) {
    this.into(2);
    this.cells.push(cell);
    this.colors.set(cell, color >>> 0);
  }

  ignite(cell: number) {
    this.into(6);
    this.cells.push(cell);
  }

  letter(index: number, state: LetterState | null) {
    if (!state) {
      this.op(3);
      this.out.u(index);
      return;
    }
    this.op(4);
    this.out.u(index);
    this.out.u(state.pinned ? 1 : 0);
    this.out.f(state.vx, 10);
    this.out.f(state.vy, 10);
    this.out.f(state.av, 100);
    this.out.f(state.burn, 1000);
  }

  block(index: number, vx: number, vy: number, av: number) {
    this.op(5);
    this.out.u(index);
    this.out.f(vx, 10);
    this.out.f(vy, 10);
    this.out.f(av, 100);
  }

  ctx(kind: number, a = 0, b = 0, c = 0, d = 0) {
    const key = `${kind},${a},${b},${c},${d}`;
    if (key === this.lastCtx) return;
    this.lastCtx = key;
    this.op(7);
    this.out.u(kind);
    for (const value of [a, b, c, d]) this.out.f(value, 1000);
  }

  heat(cell: number, a: number, b: number, flag: boolean) {
    this.op(8);
    this.out.u(cell);
    this.out.f(a, 100);
    this.out.f(b, 100);
    this.out.u(flag ? 1 : 0);
  }

  take(): Uint8Array | null {
    this.flush();
    this.lastCtx = "";
    if (!this.out.n) return null;
    const bytes = this.out.buf.slice(0, this.out.n);
    this.out.n = 0;
    return bytes;
  }
}

export function readOps(bytes: Uint8Array, sink: TerrainSink) {
  const reader = new Reader(bytes);
  while (!reader.done) {
    const op = reader.u();
    switch (op) {
      case 1:
      case 2:
      case 6: {
        const count = reader.u();
        let cell = 0;
        for (let i = 0; i < count; i++) {
          cell += reader.u();
          if (op === 1) sink.clear(cell);
          else if (op === 2) sink.debris(cell, reader.u() >>> 0);
          else sink.ignite(cell);
        }
        break;
      }
      case 3:
        sink.letter(reader.u(), null);
        break;
      case 4: {
        const index = reader.u();
        const pinned = reader.u() === 1;
        sink.letter(index, {
          pinned,
          vx: reader.f(10),
          vy: reader.f(10),
          av: reader.f(100),
          burn: reader.f(1000),
        });
        break;
      }
      case 5: {
        const index = reader.u();
        sink.block(index, reader.f(10), reader.f(10), reader.f(100));
        break;
      }
      case 7: {
        const kind = reader.u();
        sink.ctx(kind, reader.f(1000), reader.f(1000), reader.f(1000), reader.f(1000));
        break;
      }
      case 8: {
        const cell = reader.u();
        const a = reader.f(100);
        const b = reader.f(100);
        sink.heat(cell, a, b, reader.u() === 1);
        break;
      }
      default:
        throw new Error(`unknown terrain op ${op}`);
    }
  }
}

export function bytesToB64(bytes: Uint8Array): string {
  let text = "";
  for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(text);
}

export function b64ToBytes(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
